import React from "react";
import { Award, ExternalLink, Download, FileText, ShieldCheck, ArrowRight, Building2, GraduationCap } from "lucide-react";

const CENTRO_ESSE_URL = "https://server.esserete.it/fmi/webd/Esse_Gestionale?script=iscrizionesocioweb&param=B0196";
const ESSE_ORGANIZER_URL = "https://server.esserete.it/EsseOrganizer.html";
const REGISTRO_COSTO_EUR = 69;
const API = process.env.REACT_APP_BACKEND_URL;

export default function ProcedureMimitSection() {
  return (
    <div className="space-y-6" data-testid="procedure-mimit-section">
      {/* Header */}
      <div className="relative overflow-hidden rounded-2xl border-2 border-[#0a1f55]/20 bg-gradient-to-br from-[#0a1f55] via-[#102a6b] to-[#0a1f55] p-6 md:p-8 text-white">
        <div className="absolute -top-10 -right-10 h-40 w-40 rounded-full bg-[#f59e0b]/20 blur-3xl pointer-events-none" />
        <div className="relative flex items-start gap-4">
          <div className="h-14 w-14 shrink-0 rounded-2xl bg-[#f59e0b] text-[#0a1020] flex items-center justify-center shadow-lg">
            <Building2 className="h-7 w-7" />
          </div>
          <div className="flex-1">
            <div className="text-[11px] font-extrabold uppercase tracking-[0.2em] text-[#f59e0b]">
              Riconoscimento professionale
            </div>
            <h1 className="mt-1 text-[22px] md:text-[26px] font-extrabold leading-tight">
              Procedure Riconoscimento Aggregazione MIMIT
            </h1>
            <p className="mt-2 text-[13.5px] md:text-[14.5px] text-white/85 leading-relaxed max-w-2xl">
              Diventa <strong>Consulente ed Educatore Digitale, Tecnologico e di Sostenibilità</strong> con riconoscimento tramite Aggregazione MIMIT (Ministero delle Imprese e del Made in Italy) in 3 passi ufficiali.
            </p>
          </div>
        </div>
      </div>

      {/* PDF Guida */}
      <div className="rounded-2xl border-2 border-[#f59e0b]/40 bg-gradient-to-r from-[#fff7ed] via-white to-[#fef3c7] p-5 md:p-6 flex flex-col md:flex-row md:items-center gap-4">
        <div className="h-12 w-12 shrink-0 rounded-xl bg-[#f59e0b] text-[#0a1020] flex items-center justify-center">
          <FileText className="h-6 w-6" />
        </div>
        <div className="flex-1">
          <div className="text-[11px] font-extrabold uppercase tracking-[0.18em] text-[#b45309]">
            Documento ufficiale
          </div>
          <h3 className="mt-0.5 font-extrabold text-[#0a1f55]">
            Guida Riconoscimento MIMIT (PDF)
          </h3>
          <p className="text-[13px] text-slate-600 mt-1">
            Tutte le procedure passo-passo, con link ai portali Esse Studi e recap operativo.
          </p>
        </div>
        <a
          href={`${API}/api/download/guida-mimit`}
          target="_blank"
          rel="noreferrer"
          data-testid="download-guida-mimit"
          className="inline-flex items-center justify-center gap-2 px-4 h-11 rounded-xl bg-[#0a1f55] hover:bg-[#102a6b] text-white font-extrabold text-[13.5px] transition-colors whitespace-nowrap"
        >
          <Download className="h-4 w-4" />
          Scarica guida PDF
        </a>
      </div>

      {/* 3 Steps */}
      <div className="space-y-4">
        {/* Step 1 */}
        <StepCard
          numero="1"
          kicker="Step 1 · Registro Nazionale"
          titolo="Iscriviti come socio su Centro Esse Studi"
          descrizione={
            <>Registrati come socio sul portale <strong>Centro Esse Studi</strong>, la piattaforma ufficiale convenzionata con A.C.D.T. per l&apos;iscrizione al <em>Registro Nazionale dei Consulenti ed Educatori Digitali, Tecnologici e di Sostenibilità</em>.</>
          }
          cta="Iscriviti su Centro Esse Studi"
          href={CENTRO_ESSE_URL}
          ctaIcon={ExternalLink}
          ctaVariant="primary"
          ctaTestId="mimit-btn-centro-esse"
          Icon={GraduationCap}
        />

        {/* Step 2 */}
        <StepCard
          numero="2"
          kicker="Step 2 · Attestato Aggiuntivo"
          titolo="Area Soci A.C.D.T. su Esse Studi"
          descrizione={
            <>Entra nell&apos;<strong>Area Soci A.C.D.T. su Esse Organizer</strong>. Nel <em>Corso di Abilitazione</em>, <strong>invece di rifare il corso</strong>, <strong>allega il Certificato A.C.D.T.</strong> che hai ricevuto in email dopo l&apos;esame finale. Il sistema riconoscerà la tua formazione e rilascerà un <strong>secondo attestato</strong>.</>
          }
          cta="Vai all'Area Soci Esse Organizer"
          href={ESSE_ORGANIZER_URL}
          ctaIcon={ExternalLink}
          ctaVariant="accent"
          ctaTestId="mimit-btn-esse-organizer"
          Icon={ShieldCheck}
          nota="📎 Allega il file originale ricevuto via email (Certificato_Abilitazione_[Nome]_[Cognome].pdf). Non modificarlo né rinominarlo: Esse Studi verifica autenticità e integrità."
        />

        {/* Step 3 */}
        <StepCard
          numero="3"
          kicker="Step 3 · Registrazione finale"
          titolo="Pagamento e iscrizione al Registro Nazionale"
          descrizione={
            <>Con il secondo attestato ottenuto tramite Esse Studi, procedi alla <strong>registrazione ufficiale al Registro Nazionale</strong> pagando la quota di <strong>€ {REGISTRO_COSTO_EUR}</strong> — oppure dimostrando di aver acquistato un pacchetto A.C.D.T. che include la quota.</>
          }
          Icon={Award}
          bullets={[
            "Verrai inserito nell'elenco ufficiale dei consulenti registrati",
            "Riceverai l'attestato finale di riconoscimento come figura professionale",
            "Potrai esibire il tuo profilo con riconoscimento tramite Aggregazione MIMIT",
          ]}
        />
      </div>

      {/* Support */}
      <div className="rounded-2xl border border-slate-200 bg-slate-50 p-5 text-center">
        <p className="text-[13px] text-slate-600 leading-relaxed">
          Hai dubbi sulla procedura? Scrivi a{" "}
          <a href="mailto:acdt@centrostudiesse.it" className="font-bold text-[#0a1f55] hover:underline">
            acdt@centrostudiesse.it
          </a>
          . La Segreteria A.C.D.T. ti accompagnerà in ogni fase.
        </p>
      </div>
    </div>
  );
}

function StepCard({ numero, kicker, titolo, descrizione, cta, href, ctaIcon: CtaIcon, ctaVariant, ctaTestId, Icon, nota, bullets }) {
  const btnClasses =
    ctaVariant === "accent"
      ? "bg-[#f59e0b] hover:bg-[#d97706] text-[#0a1020]"
      : "bg-[#0a1f55] hover:bg-[#102a6b] text-white";
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5 md:p-6">
      <div className="flex items-start gap-4">
        <div className="h-11 w-11 shrink-0 rounded-full bg-gradient-to-br from-[#0a1f55] to-[#102a6b] text-white font-extrabold text-lg flex items-center justify-center shadow-md">
          {numero}
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[10.5px] font-extrabold uppercase tracking-[0.18em] text-[#f59e0b]">
            {kicker}
          </div>
          <h3 className="mt-1 text-[16px] md:text-[17px] font-extrabold text-[#0a1f55] flex items-center gap-2">
            {Icon && <Icon className="h-4 w-4 text-[#0a1f55]/60" />}
            {titolo}
          </h3>
          <div className="mt-2 text-[13.5px] text-slate-600 leading-relaxed">
            {descrizione}
          </div>
          {bullets && (
            <ul className="mt-3 space-y-1.5">
              {bullets.map((b) => (
                <li key={b} className="flex items-start gap-2 text-[13px] text-slate-700">
                  <ArrowRight className="h-3.5 w-3.5 text-[#f59e0b] mt-1 shrink-0" />
                  <span>{b}</span>
                </li>
              ))}
            </ul>
          )}
          {nota && (
            <div className="mt-3 p-3 rounded-lg bg-amber-50 border-l-4 border-amber-400 text-[12.5px] text-amber-900 leading-relaxed">
              {nota}
            </div>
          )}
          {cta && href && (
            <div className="mt-4">
              <a
                href={href}
                target="_blank"
                rel="noreferrer"
                data-testid={ctaTestId}
                className={`inline-flex items-center gap-2 px-4 h-11 rounded-xl font-extrabold text-[13.5px] transition-colors ${btnClasses}`}
              >
                {cta}
                <CtaIcon className="h-4 w-4" />
              </a>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
